"use client";

import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";

export type Option = {
  value: string;
  label: string;
};

interface MultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select options...",
  className,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const handleToggle = React.useCallback(
    (value: string) => {
      const next = selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value];
      onChange(next);
    },
    [selected, onChange]
  );

  const selectedLabels = React.useMemo(
    () =>
      selected
        .map((value) => options.find((o) => o.value === value)?.label)
        .filter(Boolean)
        .join(", ") || "",
    [selected, options]
  );

  const filteredOptions = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      [o.label, o.value].some((t) => t.toLowerCase().includes(q))
    );
  }, [options, query]);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full inline-flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm",
            "hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent",
            className
          )}
        >
          <span className="truncate">
            {selected.length > 0 ? selectedLabels : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className="z-50 min-w-[var(--radix-dropdown-menu-trigger-width)] rounded-md border bg-white p-1 shadow-md"
        align="start"
      >
        <div
          className="px-2 pb-1"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            className={cn(
              "w-full h-9 rounded-md border border-gray-300 px-3 text-sm",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            )}
          />
        </div>
        <div className="max-h-64 w-[var(--radix-dropdown-menu-trigger-width)] overflow-auto">
          {filteredOptions.map((option) => {
            const isChecked = selected.includes(option.value);
            return (
              <DropdownMenu.CheckboxItem
                key={option.value}
                checked={isChecked}
                onCheckedChange={() => handleToggle(option.value)}
                onSelect={(e) => {
                  e.preventDefault();
                }}
                className={cn(
                  "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none",
                  "focus:bg-gray-100 data-[state=checked]:bg-gray-50"
                )}
              >
                <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
                  <Check className={cn("h-4 w-4", isChecked ? "opacity-100" : "opacity-0")} />
                </span>
                <span className="text-gray-700">{option.label}</span>
              </DropdownMenu.CheckboxItem>
            );
          })}
          {filteredOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">No options found.</div>
          )}
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}


